// Browser error reporting (Sentry) for every Evertech app — the three sites and
// the admin. Imported via the "./monitoring" subpath and called once from each
// app's gatsby-browser `onClientEntry`, so it never runs during SSR.
//
// Deliberately a no-op unless BOTH a DSN is configured AND this is a production
// bundle: `gatsby develop` and jest stay silent, and a build without the DSN env
// var ships exactly as before. A DSN is not a secret (it is visible in the page
// source, like the GA measurement id), so it is committed per app in
// .env.production — see netlify.toml.
//
// No ErrorBoundary: React 18 rethrows uncaught render errors, which Sentry's
// default global handlers already capture, and a boundary in the SSR-shared root
// would only add a hydration surface. Errors handled in app code (Apollo's
// errorLink, caught fetches) are NOT reported — call `captureException` there if
// one should be.
import * as Sentry from "@sentry/browser"

// Noise that is never ours: browser extensions injecting into the page.
const DENY_URLS = [
  /^chrome-extension:\/\//,
  /^moz-extension:\/\//,
  /^safari-(web-)?extension:\/\//,
]

/**
 * Netlify deploy previews and branch deploys are served from *.netlify.app;
 * the production sites only ever answer on their custom domains.
 */
export function environmentFor(hostname) {
  if (hostname === "localhost" || hostname === "127.0.0.1") return "local"
  if (hostname.endsWith(".netlify.app")) return "preview"
  return "production"
}

/**
 * @param {{ dsn?: string, app: string }} options — `app` tags every event
 *   (smith / dingwell / evertech / island / admin) so one Sentry project can hold several.
 * @returns {boolean} whether reporting was started
 */
export function initErrorReporting({ dsn, app }) {
  if (!dsn || process.env.NODE_ENV !== "production") return false
  Sentry.init({
    dsn,
    environment: environmentFor(window.location.hostname),
    denyUrls: DENY_URLS,
    // Errors only: no tracing, no replay, no cookies — nothing the consent
    // banner would need to gate. sendDefaultPii stays at its default (false).
    tracesSampleRate: 0,
    initialScope: { tags: { app } },
  })
  return true
}

export const captureException = Sentry.captureException
